<template>
  <section style="display: flex; flex-flow: column; height: 100%">
    <div ref="btns">
      <slot name="operationBtn" />
      <el-button v-if="props.ifAdd" style="margin-bottom: 16px" type="primary" @click="$emit('onAdd', 'add')">
        {{ $t("common.add") }}</el-button>
    </div>
    <el-table v-bind="$attrs" ref="commonTableRef" v-loading="props.dataListLoading" class="common-table"
      :data="props.dataList" fit highlight-current-row style="width: 100%" :border="props.needBorder"
      :rowKey="getRowKey" :row-class-name="selectedTableRow" :height="tableHeight"
      :header-cell-style="props.headerStyle" :cell-style="props.cellStyle" :show-overflow-tooltip="props.needAllTooltip"
      :tooltip-options="{
        popperClass: tooltipLimitMaxWidth ? 'max-w-[60vw]' : '',
      }" @selection-change="handleSelectionChange" @select="handleSelectChange" @sort-change="handleSortChange"
      @current-change="currentChange" @cell-click="cellClick">
      <template #empty>
        <div class="flex-center h-full">
          <el-empty :description="$t('common.noData')" />
        </div>
      </template>

      <el-table-column v-if="props.isNeedSelection" type="selection" width="50" align="center"
        :reserve-selection="isNeedReverseSelection" :selectable="selectable" />
      <el-table-column v-if="props.isRadio" width="50">
        <template #default="{ row }">
          <el-radio v-model="localSingleSelection" :label="row" />
        </template>
      </el-table-column>
      <el-table-column v-if="props.needIndex" :label="$t('common.index')" width="75" align="center">
        <template #default="scope">
          {{ scope.$index + 1 + (currentPage - 1) * pageSize }}</template></el-table-column>
      <template v-for="(colConfig, index) in configs" :key="colConfig.prop">
        <slot v-if="colConfig.slot" :name="colConfig.slot" :colConfig="colConfig" />
        <el-table-column v-else-if="colConfig.hidden !== true" align="center" v-bind="setAttr(colConfig)"
          :prop="colConfig.prop" :label="$t(colConfig.label as string)" :show-overflow-tooltip="colConfig.needTooltip
            ? colConfig.needTooltip
            : props.needAllTooltip
              ? props.needAllTooltip
              : false
            " :sortable="colConfig.sort === 1
              ? true
              : colConfig.sort === 2
                ? 'custom'
                : false
              " :min-width="colConfig.minWidth ?? ''">
          <template v-if="colConfig.cellSlot" #default="{ row, $index }">
            <slot :name="colConfig.cellSlot" :colConfig="colConfig" :row="row" :rowIndex="$index"
              :columnIndex="index" />
          </template>

          <template v-else-if="colConfig.type && !colConfig.render && !colConfig.slot" #default="{ row }">
            <component :is="colConfig.type" :row="row" :config="colConfig" :rowInfo="row"
              :actionList="colConfig.actionList" :emptyCell="props.emptyCell" @formAction="clickAction" />
          </template>
          <template v-else-if="colConfig.render" #default="{ row }">
            <div class="text-overflow-2">
              {{ colConfig.render!(row) || props.emptyCell }}
            </div>
          </template>
          <template v-else #default="{ row }">
            <div class="text-overflow-2">
              <!-- {{ row[colConfig.prop!]?.toString() || props.emptyCell }} -->
              {{ colConfig.formatter
                ? colConfig.formatter(row, colConfig, row[colConfig.prop!], index)
                : (row[colConfig.prop!] ?? props.emptyCell) }}
            </div>
          </template>
        </el-table-column>
      </template>
    </el-table>
  </section>
</template>

<script lang="tsx">
import { computed, defineComponent, ref, watch, PropType, h } from "vue";
import { ElTable } from "element-plus";
const selectedRowKey = ref<string>("");
import TableSelect from "../components/TableSelect.vue";
import TableTooltip from "../components/TableTooltip.vue";
import { useI18n } from "vue-i18n";
export default defineComponent({
  name: "common-table",
  components: {
    TableSelect,
    TableTooltip,
  },
  props: {
    colConfigs: {
      type: Array as PropType<Array<TableItemConfig>>,
      required: true,
    },
    selectedRowKey: {
      type: String,
    },
    ifAdd: {
      type: Boolean,
      default: false,
    },
    dataList: {
      type: Array as PropType<Array<any> | undefined>,
      required: true,
    },
    dataListLoading: {
      type: Boolean,
      default: false,
    },
    headerStyle: {
      type: Object,
    },
    cellStyle: {
      type: Object,
    },
    needAllTooltip: {
      type: Boolean,
      default: true,
    },
    sort: {
      type: [Boolean, String],
      default: false,
    },
    isNeedSelection: {
      type: Boolean,
      default: false,
    },
    singleSelection: {
      type: Object,
    },
    isRadio: {
      type: Boolean,
      default: false,
    },
    needIndex: {
      type: Boolean,
      default: true,
    },
    needBorder: {
      type: Boolean,
      default: false,
    },
    isNeedReverseSelection: {
      type: Boolean,
      default: false,
    },
    tableHeight: {
      type: Number,
      // default: screenHeight / 2
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    rowKey: {
      type: String,
      default: "id",
    },
    selectable: {
      type: Function as PropType<(row: any, index: number) => boolean>,
      default: () => {
        return true;
      },
    },
    tableRowClassName: {
      type: Function,
      default: () => { },
    },
    size: {
      type: String,
      default: "default",
    },
    emptyText: {
      type: String,
      default: "",
    },
    tooltipLimitMaxWidth: {
      type: Boolean,
      default: true,
    },
    emptyCell: {
      type: String,
      default: "--",
    },
  },
  emits: [
    "selection-change",
    "sort-change",
    "formAction",
    "select",
    "current-change",
    "update:singleSelection",
    "cellClick",
    "onAdd",
  ],
  setup(props, { emit }) {
    const btns = ref<any>(null);
    const localSingleSelection = ref<any>({});
    const { t } = useI18n();
    // const headerAlign = ref("center");
    const getTableHeight = (height: number) => {
      if (props.tableHeight) return props.tableHeight;
      const finallyHeight = height - (btns?.value?.offsetHeight || 50) - 20;
      return finallyHeight < 100 ? 400 : finallyHeight;
    };
    const configs = computed(() => {
      const arr: Array<TableItemConfig> = Object.assign([], props.colConfigs);
      arr.forEach((item) => {
        if (item.options || item.dictName) {
          item.type = "table-select";
        }
        if (item.tooltip) {
          item.type = "table-tooltip";
        }
      });
      return arr;
    });

    //实例暴露
    const commonTableRef = ref<InstanceType<typeof ElTable>>();
    // 单选更新值
    watch(
      () => localSingleSelection.value,
      (val) => {
        emit("update:singleSelection", localSingleSelection.value);
      }
    );

    return {
      props,
      configs,
      btns,
      getTableHeight,
      commonTableRef,
      localSingleSelection,
      // headerAlign
    };
  },
  mounted() {
    selectedRowKey.value = this.props.selectedRowKey;
    this.localSingleSelection = this.singleSelection;
  },
  methods: {
    selectedTableRow(data: any) {
      if (this.tableRowClassName) {
        const classString = this.tableRowClassName(data);

        return classString;
      }
      if (selectedRowKey.value == this.getRowKey(data.row)) {
        return "selected-row";
      }
      return "";
    },
    // clearSelection() {
    //   this.$refs.commonTableRef.clearSelection();
    // },
    // toggleRowSelection(item, btn) {
    //   this.$refs.commonTableRef.toggleRowSelection(item, btn);
    // },
    handleSelectionChange(val: number): void {
      this.$emit("selection-change", val);
    },
    handleSelectChange(selection: any, row: any) {
      this.$emit("select", { selection, row });
    },

    cellClick(row: any, column: any, cell: any, event: any) {
      this.$emit("cellClick", row, column, cell, event);
    },
    handleSortChange(val: string): void {
      this.$emit("sort-change", val);
    },
    currentChange(val: string): void {
      selectedRowKey.value = val;
      this.localSingleSelection = val;
      this.$emit("current-change", val);
    },
    getRowKey(row: { [x: string]: string }): string {
      return row[this.rowKey];
    },
    setAttr(config: TableItemConfig) {
      return Object.assign(
        config,
        this.setAttrByRender(config.render),
        this.setAttrFixed(config.fixed)
        // this.setAttrTableColumn(config.tableColumn)
      );
    },
    setAttrTableColumn(tableColumn: any) {
      if (tableColumn) {
        return { ...tableColumn };
      } else {
        return {};
      }
    },
    setAttrFixed(fixed: any) {
      if (fixed) {
        return { fixed };
      }
    },
    setAttrByRender(render: any) {
      if (!render) {
        return {};
      }
      return {
        formatter: render,
      };
    },
    upperCase(value: string): string {
      if (!value) {
        return "";
      }
      return value?.toUpperCase();
    },
    clickAction<T>(item: any, rowInfo: T): void {
      this.$emit("formAction", item, rowInfo);
    },
  },
});
</script>
<style lang="scss">
.flex-button {
  display: flex;
  justify-content: space-around;
}

.text-overflow-in-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

:deep(.el-table__empty-block) {
  height: 240px !important;
}

:deep(.common-table-row) {
  @media screen and (width <=1920px) {
    .el-table__cell {
      padding: 2px 0;
    }
  }

  td {
    height: 20px;
  }

  .el-table__cell {
    padding: 5px 0;
  }
}

:deep(.el-radio__label) {
  display: none;
}
</style>
<style lang="scss">
.common-table .el-table__cell,
.common-table .is-center {
  // text-align: left !important;

  .cell {
    // float: left;
    // text-align: left;

    .el-button--primary .is-link {
      padding-left: 0;
    }
  }
}

.list-wrap .common-table th.el-table__cell {
  height: 40px;
}

.list-wrap .common-table .el-table__cell {
  height: 40px;
  padding: 0;

  .cell {
    float: unset;
    word-break: break-word;

    // display: flex;
    // justify-content: flex-start;
    // align-items: center;
  }
}

.el-table .selected-row {
  --el-table-tr-bg-color: var(--el-color-primary-light-9);
}

html:not(.dark) .common-table thead tr>th.el-table__cell,
html:not(.dark) .common-table .el-table__header-wrapper tr th.el-table-fixed-column--right {
  padding: 6px;
  background-color: rgb(234 237 243);
  // border-right: 1px solid rgb(219 219 219);
}
</style>
